import { useEffect, useMemo, useState } from "react"
import { useCategorySelection } from "hooks/categorySelection.hook"
import { isCountertop } from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import { OTHER_TOP_LEVEL_CATEGORY, OTHER_TOP_LEVEL_OPTIONS } from "./furniture.constants"
import type { TFurniture, TSelectedModelNumber } from "types/elevationReferences.type"

export type TFurnitureVariant = "countertop" | "other" | "typical"

export const useFurnitureForm = (furniture: TFurniture) => {
	// Backend marks "Other" by clearing all three category levels.
	const isOtherFromBackend =
		furniture.top_level_category == null && furniture.super_category == null && furniture.category == null

	const categorySelection = useCategorySelection({
		defaultTopLevelCategory: isOtherFromBackend ? OTHER_TOP_LEVEL_CATEGORY : (furniture.top_level_category?.name ?? null),
		defaultSuperCategory: furniture.super_category?.name ?? null,
		defaultModelCategoryId: furniture.category?.id ?? null,
		defaultModelCategoryName: furniture.category?.name ?? null,
		extraTopLevelOptions: OTHER_TOP_LEVEL_OPTIONS,
	})

	const [selectedErefId, setSelectedErefId] = useState<number | null>(furniture.eref_id)
	const [width, setWidth] = useState<number | null>(furniture.width)
	const [height, setHeight] = useState<number | null>(furniture.height)
	const [depth, setDepth] = useState<number | null>(furniture.depth)
	const [adjustableShelves, setAdjustableShelves] = useState<number | null>(
		furniture.estimation_config?.adjusted_shelves ?? null,
	)
	const [hasLocks, setHasLocks] = useState<"yes" | "no">(furniture.estimation_config?.has_locks ? "yes" : "no")
	const [fillings, setFillings] = useState<number>(furniture.estimation_config?.number_of_fillers ?? 0)
	const [finishingEnds, setFinishingEnds] = useState<number>(
		furniture.estimation_config?.number_of_finishing_ends ?? 0,
	)
	const [price, setPrice] = useState<number | null>(furniture.fixed_price_config?.fixed_price ?? null)
	const [name, setName] = useState<string>(furniture.fixed_price_config?.name ?? "")
	const [notes, setNotes] = useState<string>(furniture.estimation_config_comment ?? "")
	const [selectedModelNumber, setSelectedModelNumber] = useState<TSelectedModelNumber | null>(
		furniture.selected_model_number,
	)

	const status = furniture.status ?? "work_in_progress"
	const isReviewed = status === "reviewed"
	const isInQuotation = status === "in_quotation"

	const incomingSelectedId = furniture.selected_model_number?.id ?? null
	useEffect(() => {
		setSelectedModelNumber(furniture.selected_model_number)
		// Only resync when the *id* changes to avoid loops on object-identity churn from cache splices.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomingSelectedId])

	const incomingHasLocks = furniture.estimation_config?.has_locks ?? false
	useEffect(() => {
		setHasLocks(incomingHasLocks ? "yes" : "no")
	}, [incomingHasLocks])

	const incomingAdjustedShelves = furniture.estimation_config?.adjusted_shelves ?? null
	useEffect(() => {
		setAdjustableShelves(incomingAdjustedShelves)
	}, [incomingAdjustedShelves])

	const incomingFixedPrice = furniture.fixed_price_config?.fixed_price ?? null
	useEffect(() => {
		setPrice(incomingFixedPrice)
	}, [incomingFixedPrice])

	const incomingName = furniture.fixed_price_config?.name ?? ""
	useEffect(() => {
		setName(incomingName)
	}, [incomingName])

	const isCountertopItem = isCountertop(furniture)

	const variant: TFurnitureVariant = useMemo(() => {
		if (isCountertopItem) return "countertop"
		if (categorySelection.selectedTopLevelCategory === OTHER_TOP_LEVEL_CATEGORY) return "other"
		return "typical"
	}, [categorySelection.selectedTopLevelCategory, isCountertopItem])

	const hasModelNumber = selectedModelNumber !== null

	const isValid = useMemo(() => {
		if (selectedErefId === null || categorySelection.selectedTopLevelCategory === null) return false

		if (variant === "other") return price !== null && name.trim() !== ""

		const dimensionsFilled = width !== null && height !== null && depth !== null
		if (!dimensionsFilled) return false

		const fullCategoriesFilled =
			categorySelection.selectedSuperCategory !== null && categorySelection.selectedModelCategoryId !== null
		if (!fullCategoriesFilled) return false

		if (variant === "countertop") return true
		return hasModelNumber
	}, [
		categorySelection.selectedModelCategoryId,
		categorySelection.selectedSuperCategory,
		categorySelection.selectedTopLevelCategory,
		depth,
		hasModelNumber,
		height,
		name,
		price,
		selectedErefId,
		variant,
		width,
	])

	return {
		variant,
		isCountertopItem,
		hasModelNumber,
		categorySelection,
		selectedErefId,
		setSelectedErefId,
		width,
		setWidth,
		height,
		setHeight,
		depth,
		setDepth,
		adjustableShelves,
		setAdjustableShelves,
		hasLocks,
		setHasLocks,
		fillings,
		setFillings,
		finishingEnds,
		setFinishingEnds,
		price,
		setPrice,
		name,
		setName,
		notes,
		setNotes,
		isReviewed,
		isInQuotation,
		selectedModelNumber,
		setSelectedModelNumber,
		isValid,
	}
}
